// Global Instructions Rule Applied!
import React from 'react'
import { Card, Result, Spin, Button, Typography } from 'antd'

/** Centered loading spinner for a screen or section. */
export function LoadingState({ label }: { label?: string }): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 16px',
        color: 'rgba(0,0,0,0.45)'
      }}
    >
      <Spin size="large" />
      {label ? <span>{label}</span> : null}
    </div>
  )
}

/** Friendly error panel with an optional retry action. */
export function ErrorState({
  message,
  onRetry
}: {
  message?: string | null
  onRetry?: () => void
}): React.JSX.Element {
  return (
    <Result
      status="warning"
      title="Couldn't load this"
      subTitle={message || 'Something went wrong. Please try again.'}
      extra={
        onRetry ? (
          <Button type="primary" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  )
}

/** Centered small spinner for a section body while its lazy fetch runs. */
export function SectionSpinner(): React.JSX.Element {
  return (
    <div style={{ textAlign: 'center', padding: '12px 0' }}>
      <Spin size="small" />
    </div>
  )
}

/** Compact in-section load failure with a retry action (keeps the rest of the screen usable). */
export function SectionError({
  message,
  onRetry
}: {
  message?: string | null
  onRetry: () => void
}): React.JSX.Element {
  return (
    <div style={{ padding: '4px 0' }}>
      <Typography.Text type="secondary">{message || "Couldn't load this section."}</Typography.Text>
      <div style={{ marginTop: 8 }}>
        <Button size="small" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  )
}

/** A titled card used to group fields on a detail screen. */
export function Section({
  title,
  extra,
  children
}: {
  title?: React.ReactNode
  extra?: React.ReactNode
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <Card
      size="small"
      title={title}
      extra={extra}
      style={{ marginBottom: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      {children}
    </Card>
  )
}
